import { useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { App } from 'antd'
import { useUploadStore } from '@/store'
import { useShallow } from 'zustand/react/shallow'
import { DEFAULT_APP_ICON, isAppIconSrc } from '@/lib/appIcon'
import type { AppInfo, MainPackageInfo } from '@/types'
import tgzIcon from '@/assets/tgz-package-icon.png'
import errorIcon from '@/assets/upload-error-icon.svg'
import parsingIcon from '@/assets/parsing-sending-icon.svg'
import RefreshIcon from '@/assets/reupload-refresh.svg?react'
import DocumentUploadIcon from '@/assets/document-upload.svg?react'
import { isTgzFile } from '../packageFile'
import { ActionButton } from './parts'

export default function MainPackageSection({
  app,
  committed,
  readOnly,
  uploadContext,
  beforeStart,
}: {
  /** 创建应用首包解析前可能尚无应用记录 */
  app?: AppInfo
  committed?: MainPackageInfo
  readOnly?: boolean
  /** 迭代 / 同草稿覆盖时带给上传接口 */
  uploadContext?: { app_id?: string; version_id?: string }
  /** 开始上传前的拦截（如：还有子包在传时先确认中断） */
  beforeStart?: (proceed: () => void) => void
}) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const { tasks, startMain, retryTask, cancelTask } = useUploadStore(
    useShallow((s) => ({
      tasks: s.tasks,
      startMain: s.startMain,
      retryTask: s.retryTask,
      cancelTask: s.cancelTask,
    })),
  )
  const mainTask = tasks.find((x) => {
    if (x.kind !== 'main') return false
    // 配置抽屉始终带版本号，不能把还没落版本的新建主包进度画到这份草稿上
    if (uploadContext?.version_id) return x.version_id === uploadContext.version_id
    return !x.version_id
  })

  const pick = (file?: File | null) => {
    if (!file || readOnly) return
    if (!isTgzFile(file)) {
      message.error(t('versionForm.unsupportedPackageFormat'))
      return
    }
    const start = () => startMain(file, uploadContext)
    if (beforeStart) beforeStart(start)
    else start()
  }

  const openPicker = () => inputRef.current?.click()

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept=".tgz"
      hidden
      onChange={(e) => {
        pick(e.target.files?.[0])
        e.target.value = ''
      }}
    />
  )

  // 已提交主包（或解析完成）→ 信息卡
  if (committed && (!mainTask || mainTask.status === 'success')) {
    return (
      <div
          style={{
            border: '1px solid var(--color-border-line)',
            borderRadius: 20,
            padding: 20,
            background: 'var(--color-background-module-container)',
            boxShadow: '2px 2px 30px rgba(0, 0, 0, 0.05)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                width: 64,
                height: 64,
                flexShrink: 0,
                borderRadius: 14,
                background: '#fff',
                border: '1px solid var(--color-border-card)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 34,
                overflow: 'hidden',
              }}
            >
              {isAppIconSrc(app?.icon || DEFAULT_APP_ICON) ? (
                <img
                  src={app?.icon || DEFAULT_APP_ICON}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                app?.icon
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div
                title={committed.file_name}
                style={{
                  fontWeight: 500,
                  fontSize: 15,
                  lineHeight: '24px',
                  color: '#000',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {committed.file_name}
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: '#000',
                  lineHeight: '22px',
                }}
              >
                {committed.size_mb} MB
              </div>
            </div>
            {!readOnly && (
              <ActionButton icon={<RefreshIcon className="size-4" />} onClick={openPicker}>
                {t('versionForm.reupload')}
              </ActionButton>
            )}
          </div>
          <div
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: '1px solid rgba(0,0,0,0.04)',
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              rowGap: 6,
              columnGap: 10,
              fontSize: 14,
              lineHeight: '22px',
            }}
          >
            <span style={infoLabelStyle}>
              {t('versionForm.appName')}:
            </span>
            <span
              title={app?.name}
              style={{ ...ellipsisValue, fontWeight: 500, color: '#000' }}
            >
              {app?.name ?? '—'}
            </span>
            <span style={infoLabelStyle}>
              {t('versionForm.moduleId')}:
            </span>
            <span
              title={app?.module_id || app?.app_id}
              style={{
                ...ellipsisValue,
                fontWeight: 400,
                color: '#000',
                fontFamily: 'ui-monospace, monospace',
              }}
            >
              {app?.module_id || app?.app_id || '—'}
            </span>
            <span style={infoLabelStyle}>
              {t('versionForm.versionCode')}:
            </span>
            <span style={{ fontWeight: 400, color: '#000' }}>
              {committed.version_code}
            </span>
          </div>
          {fileInput}
        </div>
    )
  }

  // 上传中
  if (mainTask?.status === 'uploading') {
    return (
      <>
        <OuterShell>
          <div style={innerCenterStyle}>
            <PackageIcon />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 16,
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <span
                  title={mainTask.file_name}
                  style={{
                    fontSize: 14,
                    lineHeight: '22px',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {mainTask.file_name}
                  <span style={{ marginLeft: 10 }}>{mainTask.size_mb} MB</span>
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => cancelTask(mainTask.id)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--color-brand-indigo)',
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                      padding: 0,
                      flexShrink: 0,
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  width: '100%',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    maxWidth: 420,
                    height: 6,
                    borderRadius: 8,
                    background: 'rgba(140, 140, 140, 0.16)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${mainTask.progress}%`,
                      height: '100%',
                      borderRadius: 8,
                      background: 'var(--accent)',
                      transition: 'width 0.2s',
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: '18px',
                    color: '#000',
                    minWidth: 36,
                  }}
                >
                  {mainTask.progress}%
                </span>
              </div>
            </div>
          </div>
        </OuterShell>
        {fileInput}
      </>
    )
  }

  // 解析中
  if (mainTask?.status === 'parsing') {
    return (
      <>
        <OuterShell>
          <div style={innerCenterStyle}>
            <PackageIcon />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0,
                width: '100%',
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  lineHeight: '22px',
                  marginBottom: 0,
                }}
              >
                {mainTask.file_name}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginTop: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    lineHeight: '22px',
                    color: 'rgba(0,0,0,0.4)',
                  }}
                >
                  {t('versionForm.parsing')}
                </span>
                <img
                  src={parsingIcon}
                  alt=""
                  width={24}
                  height={24}
                  style={{
                    animation: 'version-spin 1s linear infinite',
                  }}
                />
              </div>
            </div>
          </div>
        </OuterShell>
        {fileInput}
      </>
    )
  }

  // 上传失败 / 解析失败 — Figma 10051:174150 总高 244
  if (mainTask && (mainTask.status === 'failed' || mainTask.status === 'parse_failed')) {
    const isUploadFail = mainTask.status === 'failed'
    return (
      <>
        <OuterShell>
          <div style={innerCenterStyle}>
            <PackageIcon />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 16,
                  fontSize: 14,
                  lineHeight: '22px',
                }}
              >
                <span>{mainTask.file_name}</span>
                <span>{mainTask.size_mb} MB</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    lineHeight: '22px',
                    color: 'var(--color-status-danger)',
                    textAlign: 'center',
                  }}
                >
                  {mainTask.error_message?.trim() ||
                    (isUploadFail
                      ? t('versionForm.uploadFailed')
                      : t('versionForm.parseFailed'))}
                </span>
                <img src={errorIcon} alt="" width={24} height={24} />
              </div>
            </div>
            {!readOnly && (
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  justifyContent: 'center',
                }}
              >
                <ActionButton
                  icon={<RefreshIcon className="size-4" />}
                  onClick={() =>
                    isUploadFail ? retryTask(mainTask.id) : openPicker()
                  }
                >
                  {t('versionForm.reupload')}
                </ActionButton>
                {isUploadFail && (
                  <ActionButton
                    icon={<DocumentUploadIcon className="size-4" />}
                    onClick={openPicker}
                  >
                    {t('versionForm.selectAnother')}
                  </ActionButton>
                )}
              </div>
            )}
          </div>
        </OuterShell>
        {fileInput}
      </>
    )
  }

  // 初始拖拽区
  return (
    <>
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          pick(e.dataTransfer.files?.[0])
        }}
        onClick={() => !readOnly && openPicker()}
        style={{
          border: '1px solid var(--color-border-line)',
          borderRadius: 20,
          padding: 20,
          cursor: readOnly ? 'default' : 'pointer',
          background: 'var(--color-background-module-muted)',
        }}
      >
        <div
          style={{
            border: '1px dashed var(--color-brand-indigo)',
            borderRadius: 14,
            padding: '20px 12px',
            textAlign: 'center',
            background: dragOver
              ? '#fff'
              : 'var(--color-background-module-container)',
            transition: 'background 0.15s',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <PackageIcon opacity={0.6} />
          <div>
            <div style={{ color: '#000', fontSize: 14, lineHeight: '22px' }}>
              {t('versionForm.dropMainPackage')}
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'rgba(0,0,0,0.4)',
                lineHeight: '20px',
                marginTop: 0,
              }}
            >
              {t('versionForm.mainPackageHint')}
            </div>
          </div>
        </div>
        {fileInput}
      </div>
    </>
  )
}

const ellipsisValue: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

/** Figma：参数名 Regular 14 / rgba(0,0,0,0.6) */
const infoLabelStyle: CSSProperties = {
  color: 'rgba(0, 0, 0, 0.6)',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: '22px',
}

const innerCenterStyle: CSSProperties = {
  padding: '20px 12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
}

function OuterShell({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        border: '1px solid var(--color-border-line)',
        borderRadius: 20,
        padding: 20,
        background: 'var(--color-background-module-muted)',
      }}
    >
      {children}
    </div>
  )
}

function PackageIcon({ opacity = 1 }: { opacity?: number }) {
  return (
    <img
      src={tgzIcon}
      alt=""
      width={64}
      height={64}
      style={{ display: 'block', opacity, objectFit: 'contain' }}
    />
  )
}
